import { BeneficiaryAggregateNumberDimension } from '@repo/shared/dtos/beneficiary'
import { Button } from '@repo/ui/button'
import { ColorPicker } from '@repo/ui/color-picker'
import { useT } from '@repo/ui/hooks'
import { Input } from '@repo/ui/input'
import { Plus, Trash2 } from 'lucide-react'
import { Fragment, useMemo } from 'react'

export default function RangeNumberBuckets({
  dimension,
  onChange,
}: {
  dimension: BeneficiaryAggregateNumberDimension
  onChange: (dimension: BeneficiaryAggregateNumberDimension) => void
}) {
  const { t } = useT({ keyPrefix: 'admin.insights.form.dimensions.buckets.number' })
  const buckets = useMemo(() => dimension.buckets, [dimension])

  const updateProperty = (
    index: number,
    property: 'label' | 'min' | 'max' | 'color',
    value: string,
  ) => {
    onChange({
      ...dimension,
      buckets: buckets.map((b, i) => (i === index ? { ...b, [property]: value } : b)),
    })
  }

  const addBucket = () => {
    onChange({
      ...dimension,
      buckets: [...buckets, { label: '', min: undefined, max: undefined }],
    })
  }

  const deleteBucket = (index: number) => {
    onChange({
      ...dimension,
      buckets: buckets.filter((_, i) => i !== index),
    })
  }

  return (
    <div className="space-y-2">
      <div className="grid [grid-template-columns:2fr_2fr_2fr_3rem_3rem] gap-2">
        <div className="text-sm font-medium text-primary-600">{t('columns.label')}</div>
        <div className="text-sm font-medium text-primary-600">{t('columns.min')}</div>
        <div className="text-sm font-medium text-primary-600">{t('columns.max')}</div>
        <div className="text-sm font-medium text-primary-600">{t('columns.color')}</div>
        <div></div>
        {buckets.map((bucket, index) => (
          <Fragment key={index}>
            <Input
              key={`label-${index}`}
              id={`label-${index}`}
              type="text"
              placeholder={t('labelPlaceholder')}
              value={bucket.label}
              onChange={(event) => updateProperty(index, 'label', event.target.value)}
            />
            <Input
              key={`min-${index}`}
              id={`min-${index}`}
              type="number"
              placeholder={t('minPlaceholder')}
              value={bucket.min?.toString() ?? ''}
              onChange={(event) => updateProperty(index, 'min', event.target.value)}
            />
            <Input
              key={`max-${index}`}
              id={`max-${index}`}
              type="number"
              placeholder={t('maxPlaceholder')}
              value={bucket.max?.toString() ?? ''}
              onChange={(event) => updateProperty(index, 'max', event.target.value)}
            />
            <ColorPicker
              color={bucket.color}
              onChange={(value) => updateProperty(index, 'color', value)}
            />
            <Button variant="outline" size="sm" onClick={() => deleteBucket(index)}>
              <Trash2 className="w-4 h-4" />
            </Button>
          </Fragment>
        ))}
      </div>
      <Button variant="outline" size="sm" onClick={() => addBucket()} className="w-full">
        <Plus className="w-4 h-4" />
        {t('addBucket')}
      </Button>
    </div>
  )
}
